import React, { useEffect, useReducer, useCallback } from 'react';
import GridComponent from './GridComponent';
// [REMOVED - MAR 2026]: GridComponentSimple merged into GridComponent
import { systemBus, EVENTS } from '../services/systemBus';
import { Z } from '../constants/zLayers';
import useIsMobile from '../hooks/useIsMobile';
import { useCharacter } from '../contexts/CharacterContext';
import { useModalEscape } from '../hooks/useModalEscape';

const ContainerModal = ({
  openContainer,
  setOpenContainer,
  showVaultModal,
  inventory,
  gridCols,
  gridRows,
  draggedItem,
  hoveredGrid,
  hoveredCell,
  selectedItem,
  setSelectedItem,
  selectedItemSource,
  setSelectedItemSource,
  moveItem,
  isValidPosition,
  handleMouseDown,
  handleContextMenu,
  handleCellClick,
  handleTouchMove,
  showStatus,
  setEditingItem,
  settings,
  InventoryItem
}) => {
  // [SYSTEM UNIFICATION - OCT 25, 2025]: Force re-render mechanism for event subscriptions
  // PURPOSE: Ensures container capacity and weight displays update when contents change
  const [, forceUpdate] = useReducer(x => x + 1, 0);
  const isMobile = useIsMobile();
  const { logActivity } = useCharacter();

  // [APR 2026]: Standardize Escape dismissal via useModalEscape hook.
  // [WHY]: Wrap setOpenContainer(null) so the hook gets a stable function ref;
  //   useCallback prevents re-binding the listener every render.
  const handleCloseContainer = useCallback(() => setOpenContainer(null), [setOpenContainer]);
  useModalEscape(Boolean(openContainer), handleCloseContainer);

  // [SYSTEM UNIFICATION - OCT 25, 2025]: Event subscriptions for reactive container updates
  // PURPOSE: Container modal re-renders when items are added/removed from container
  // WHY: Ensures capacity displays (lines 54-60) and weight calculations (lines 169-180) stay accurate
  // INTEGRATION: Complements GridComponent subscriptions, handles container-specific weight display
  useEffect(() => {
    const handleContainerChange = (eventName, data) => {
      forceUpdate();

      // [FIX APR 2026]: Log meaningful container transfer actions
      if (eventName === 'INVENTORY_ITEM_MOVED' && data?.item) {
        const containerId = openContainer?.id;
        const sourceIsContainer = data.source === `container_${containerId}`;
        const targetIsContainer = data.target === `container_${containerId}`;
        if (sourceIsContainer && !targetIsContainer) {
          logActivity(`Moved ${data.item.name} out of ${openContainer?.name || 'container'}`, 'inventory', {
            itemName: data.item.name, containerName: openContainer?.name, direction: 'out'
          });
        } else if (!sourceIsContainer && targetIsContainer) {
          logActivity(`Moved ${data.item.name} into ${openContainer?.name || 'container'}`, 'inventory', {
            itemName: data.item.name, containerName: openContainer?.name, direction: 'in'
          });
        }
      }
    };

    // Subscribe to container-affecting events
    const unsubscribeItemAdded = systemBus.on(EVENTS.INVENTORY_ITEM_ADDED, (data) =>
      handleContainerChange('INVENTORY_ITEM_ADDED', data));
    const unsubscribeItemRemoved = systemBus.on(EVENTS.INVENTORY_ITEM_REMOVED, (data) =>
      handleContainerChange('INVENTORY_ITEM_REMOVED', data));
    const unsubscribeItemMoved = systemBus.on(EVENTS.INVENTORY_ITEM_MOVED, (data) =>
      handleContainerChange('INVENTORY_ITEM_MOVED', data));

    // Cleanup: Unsubscribe on component unmount to prevent memory leaks
    return () => {
      unsubscribeItemAdded();
      unsubscribeItemRemoved();
      unsubscribeItemMoved();
    };
  }, [openContainer?.id, openContainer?.name, logActivity]); // Re-subscribe when active container changes

  if (!openContainer) return null;
  
  const container = inventory.find(i => i.id === openContainer.id);
  if (!container) {
    setOpenContainer(null);
    return null;
  }
  
  // Higher z-index if vault is also open
  const zIndex = showVaultModal ? Z.CONTAINER_OVER_VAULT : Z.CONTAINER_MODAL;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90" data-testid="container-modal" style={{ backgroundColor: 'rgba(0,0,0,0.95)', zIndex }}>
      <div className="fixed inset-4 bg-gray-900 rounded-lg flex flex-col">
        <div className="bg-gray-800 p-3 border-b-2 border-gray-700 flex justify-between items-center rounded-t-lg">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center">
              {container.icon} {container.name}
              {container.extradimensional && <span className="ml-2 text-xs bg-purple-700 px-2 py-1 rounded">Extradimensional</span>}
              {container.cursed && <span className="ml-2 text-xs bg-red-700 px-2 py-1 rounded">Cursed</span>}
            </h2>
            {container.maxWeight && (
              <div className="text-xs text-gray-400 mt-1">
                Capacity: {(() => {
                  const currentWeight = container.containedItems?.reduce((sum, item) => sum + (item.weight || 0), 0) || 0;
                  return `${currentWeight.toFixed(1)} / ${container.maxWeight} lbs`;
                })()}
                {container.weightReduction > 0 && ` (${(container.weightReduction * 100).toFixed(0)}% weight reduction)`}
              </div>
            )}
          </div>
          <button 
            onClick={() => setOpenContainer(null)}
            className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-2 p-3 bg-amber-800 rounded text-sm text-white">
            <strong>{'\uD83D\uDCE6'} Transfer Mode:</strong> {
              isMobile && settings?.mobileInteractionMode === 'tap'
                ? 'Tap an item to select it, then tap an empty cell to move it'
                : 'Drag items between inventory and container'
            }
          </div>
          
          {/* [UNIFIED GRID - MAR 2026]: Single GridComponent with mode flag */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <GridComponent
              gridId="inventory"
              cols={gridCols}
              rows={gridRows}
              items={inventory}
              title={`Main Inventory (${gridCols}x${gridRows})`}
              selectedItem={selectedItem}
              setSelectedItem={setSelectedItem}
              selectedItemSource={selectedItemSource}
              setSelectedItemSource={setSelectedItemSource}
              isValidPosition={isValidPosition}
              handleMouseDown={handleMouseDown}
              handleContextMenu={handleContextMenu}
              handleTouchMove={handleTouchMove}
              handleCellClick={handleCellClick}
              setOpenContainer={setOpenContainer}
              setEditingItem={setEditingItem}
              InventoryItem={InventoryItem}
              enableDragDrop={!(isMobile && settings?.mobileInteractionMode === 'tap')}
            />

            <GridComponent
              gridId={`container_${container.id}`}
              cols={container.containerSlots[0]}
              rows={container.containerSlots[1]}
              items={container.containedItems || []}
              title={`${container.name} Contents (${container.containerSlots[0]}x${container.containerSlots[1]})`}
              selectedItem={selectedItem}
              setSelectedItem={setSelectedItem}
              selectedItemSource={selectedItemSource}
              setSelectedItemSource={setSelectedItemSource}
              isValidPosition={isValidPosition}
              handleMouseDown={handleMouseDown}
              handleContextMenu={handleContextMenu}
              handleTouchMove={handleTouchMove}
              handleCellClick={handleCellClick}
              setOpenContainer={setOpenContainer}
              setEditingItem={setEditingItem}
              InventoryItem={InventoryItem}
              enableDragDrop={!(isMobile && settings?.mobileInteractionMode === 'tap')}
            />
          </div>
        </div>
        
        <div className="p-3 bg-gray-800 text-sm text-gray-400 rounded-b-lg border-t border-gray-700">
          <div className="flex justify-between">
            <span>
              Container Weight: {container.weight}lb base + {
                (container.containedItems || []).reduce((sum, i) => sum + (i.weight || 0), 0).toFixed(1)
              }lb contents
              {container.weightReduction > 0 && ` (${container.weightReduction * 100}% reduction)`}
            </span>
            <span>
              Total: {(() => {
                const containerWithMethods = Object.assign(Object.create(InventoryItem.prototype), container);
                return containerWithMethods.getTotalWeight().toFixed(1);
              })()}lb
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContainerModal;